import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { del, post, put } from "../../lib/api";
import {
  PUSH_TOKEN_MAX,
  PUSH_URL_MAX,
  pushBacklogSummary,
  pushTokenRequired,
  validatePushUrl,
} from "../../lib/apps";
import { count, timestamp } from "../../lib/format";
import type { AppDetail, AppPushBacklog, AppPushDetail } from "../../lib/types";
import { Button } from "../ui/Button";
import { EmptyState, ErrorNotice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { Datum, MicroLabel, SectionTitle } from "../ui/Text";

/**
 * Where this app's closed sessions are sent (docs/session-push-plan.md).
 *
 * The bearer token goes one way: it is sealed on the server and never read back,
 * so the panel only says whether one is set. Leaving the token empty on an edit
 * keeps the stored one, which is how a URL is corrected without asking the
 * partner for their token again — on the same host only, so the token is never
 * sent somewhere it was not entered for.
 */

export interface AppPushProps {
  readonly app: AppDetail;
  readonly timeZone: string;
  readonly canEdit: boolean;
  readonly onChanged: (message: string) => void;
}

export function AppPush({ app, timeZone, canEdit, onChanged }: AppPushProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const push = app.push;

  const remove = useMutation({
    mutationFn: () => del<AppDetail>(`/apps/${app.id}/push`),
    onSuccess: () => {
      setConfirmingRemove(false);
      onChanged("Push removed. Sessions already queued for it are parked, not sent.");
    },
  });

  return (
    <section aria-labelledby="app-push" className="flex flex-col gap-4">
      <SectionTitle
        action={
          canEdit && !isEditing ? (
            <Button
              variant={push === null ? "primary" : "ghost"}
              onClick={() => setIsEditing(true)}
            >
              {push === null ? "Set up push" : "Edit push"}
            </Button>
          ) : undefined
        }
      >
        <span id="app-push">Session push</span>
      </SectionTitle>

      {isEditing ? (
        <PushForm
          app={app}
          onSaved={() => {
            setIsEditing(false);
            onChanged("Push saved. Sessions that close from now on are sent to it.");
          }}
          onCancel={() => setIsEditing(false)}
        />
      ) : null}

      {push === null ? (
        isEditing ? null : (
          <EmptyState
            title="Not pushing."
            detail="Closed sessions this app can see are sent nowhere until a push URL is set."
          />
        )
      ) : (
        <>
          <dl className="m-0 grid grid-cols-1 gap-4 md:grid-cols-3">
            <Datum label="Endpoint" mono>
              <span className="break-all">{push.url}</span>
            </Datum>
            <Datum label="Bearer token">{push.tokenSet ? "Set" : "Not set"}</Datum>
            <Datum label="Changed">
              {push.updatedAt === null ? "—" : timestamp(push.updatedAt, timeZone)}
            </Datum>
          </dl>
          <Link
            to={`/session-pushes?appId=${encodeURIComponent(app.id)}`}
            className="self-start text-data text-signal underline decoration-1 underline-offset-4 hover:no-underline"
          >
            Every request and answer
          </Link>
          <PushBacklog app={app} push={push} canEdit={canEdit} onChanged={onChanged} />
          {remove.isError ? <ErrorNotice error={remove.error} /> : null}
          {canEdit ? (
            <div className="flex flex-wrap items-center gap-2">
              {confirmingRemove ? (
                <>
                  <Button
                    variant="danger"
                    pending={remove.isPending}
                    onClick={() => remove.mutate()}
                  >
                    Stop pushing
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmingRemove(false)}>
                    Keep
                  </Button>
                </>
              ) : (
                <Button variant="ghost" onClick={() => setConfirmingRemove(true)}>
                  Remove push
                </Button>
              )}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/** The worker's pace: 20 rows every 30 s (`server/src/domain/sessionPush/worker.ts`). */
const PUSHES_PER_MINUTE = 40;

/**
 * "Push all unpushed": sessions that closed before the push was set are never
 * queued on their own, and parked ones wait for a person. One press queues both;
 * the worker sends them at its usual pace, and anything that closes later still
 * goes ahead of them.
 */
function PushBacklog({
  app,
  push,
  canEdit,
  onChanged,
}: {
  readonly app: AppDetail;
  readonly push: AppPushDetail;
  readonly canEdit: boolean;
  readonly onChanged: (message: string) => void;
}) {
  const [isConfirming, setIsConfirming] = useState(false);
  const summary = pushBacklogSummary(push.backlog);
  const minutes = Math.ceil(summary.total / PUSHES_PER_MINUTE);

  const queue = useMutation({
    mutationFn: () => post<AppPushBacklog>(`/apps/${app.id}/push/backlog`, {}),
    onSuccess: (queued) => {
      setIsConfirming(false);
      onChanged(
        `Queued ${count(queued.unsent + queued.parked)} sessions. Sessions that close from now on still go first.`,
      );
    },
  });

  return (
    <div className="flex flex-col gap-3 border-t border-rule pt-4">
      <MicroLabel>Not yet sent</MicroLabel>
      <p className="m-0 text-data text-ink">{summary.text}</p>
      {app.disabled && summary.total > 0 ? (
        <p className="m-0 text-data text-ink-faint">Enable the app to push them.</p>
      ) : null}
      {queue.isError ? <ErrorNotice error={queue.error} /> : null}
      {canEdit && summary.total > 0 && !app.disabled ? (
        <div className="flex flex-wrap items-center gap-2">
          {isConfirming ? (
            <>
              <span className="text-data text-ink-faint">
                {`About ${count(minutes)} minute${minutes === 1 ? "" : "s"} to send at ${PUSHES_PER_MINUTE} a minute.`}
              </span>
              <Button variant="primary" pending={queue.isPending} onClick={() => queue.mutate()}>
                {`Push ${count(summary.total)}`}
              </Button>
              <Button variant="ghost" onClick={() => setIsConfirming(false)}>
                Not now
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => setIsConfirming(true)}>
              Push all unpushed
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PushForm({
  app,
  onSaved,
  onCancel,
}: {
  readonly app: AppDetail;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}) {
  const [url, setUrl] = useState(app.push?.url ?? "");
  const [token, setToken] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const needsToken = pushTokenRequired(app.push, url);

  const urlError = validatePushUrl(url);
  const tokenError =
    needsToken && token.trim() === ""
      ? app.push === null
        ? "Required the first time"
        : "Required for a new host"
      : undefined;

  const save = useMutation({
    mutationFn: () =>
      put<AppDetail>(`/apps/${app.id}/push`, {
        url: url.trim(),
        ...(token.trim() === "" ? {} : { token: token.trim() }),
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>{app.push === null ? "New push" : "Edit push"}</MicroLabel>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {save.isError ? <ErrorNotice error={save.error} /> : null}
      <form
        aria-label="Session push"
        onSubmit={(event) => {
          event.preventDefault();
          setIsSubmitted(true);
          if (urlError === undefined && tokenError === undefined) save.mutate();
        }}
        className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2"
      >
        <Field
          label="Push URL"
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://partner.example.com/api/external/charge-session"
          maxLength={PUSH_URL_MAX}
          error={isSubmitted ? urlError : undefined}
          hint="The full endpoint. Each closed session is POSTed here once."
          autoFocus
        />
        <Field
          label="Bearer token"
          type="password"
          autoComplete="off"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          maxLength={PUSH_TOKEN_MAX}
          error={isSubmitted ? tokenError : undefined}
          hint={
            !needsToken
              ? "Leave empty to keep the stored token."
              : "The partner's token. Stored encrypted and never shown again."
          }
        />
        <div className="flex items-end md:col-span-2">
          <Button type="submit" variant="primary" pending={save.isPending}>
            Save push
          </Button>
        </div>
      </form>
    </div>
  );
}
